import { useState } from 'react';
import { Avatar, Box, Typography, IconButton, Menu, MenuItem, Divider, useMediaQuery, useTheme } from '@mui/material';
import { AdminPanelSettings, CollectionsBookmark, KeyboardArrowDown, Logout, Person, RateReviewOutlined, Settings, Timeline } from "@mui/icons-material";
import { useNavigate } from 'react-router-dom';
import { en } from '../translations/en';
import { useAuth } from '../hooks/useAuth';
import { usePermissions } from '../hooks/usePermissions';
import { useAliId } from '../hooks/useAliId';
import { useAuthorization } from '../hooks/useAuthorization';
import { useActiveCatalog } from '../hooks/useActiveCatalog';
import { getInitials } from '../utils/initialsUtils';
import { BETA_FEEDBACK_URL } from '../constants/feedbackForm';
import { ThemeToggle } from './ThemeToggle';

export const UserProfile = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const open = Boolean(anchorEl);
  const { permissions } = usePermissions();
  const { aliId, loading: aliIdLoading } = useAliId();
  const { isCommercialTier } = useAuthorization();
  const isCommercial = isCommercialTier();
  const { switchToOwnCatalog, manageableCatalogs = [] } = useActiveCatalog();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const { signOut, user } = useAuth();

  const getDisplayName = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user?.username || 'User';
  };

  const handleLogout = () => {
    signOut();
    handleClose();
  };

  const handleCurriculumVitae = () => {
    // ActiveCatalogContext persists across navigation via localStorage; reset
    // it here so "My CV" never shows a previously-viewed delegator's content.
    switchToOwnCatalog();
    navigate('/curriculum-vitae');
    handleClose();
  };

  const handleSettings = () => {
    // For viewers and delegates, navigate to settings with account tab only
    if (permissions?.role === 'viewer' || permissions?.role === 'delegate') {
      navigate('/settings?tab=account');
    } else {
      navigate('/settings');
    }
    handleClose();
  };

  const handleAdmin = () => {
    navigate('/admin');
    handleClose();
  };

  const handleCollections = () => {
    navigate('/albums');
    handleClose();
  };

  const handleActivities = () => {
    navigate('/activities');
    handleClose();
  };

  const handleBetaFeedback = () => {
    window.open(BETA_FEEDBACK_URL, '_blank', 'noopener,noreferrer');
    handleClose();
  };

  const isViewer = permissions?.role === 'viewer';
  const isDelegate = permissions?.role === 'delegate';
  const isAdmin = permissions?.role === 'admin';
  // Non-delegates (owners/admins) always manage their own catalog, so the gate
  // stays synchronous (no dependency on the async owned-catalogs fetch — avoids
  // a flash-hidden Albums item, or losing it entirely if both subscription and
  // ali-id fetches fail). Delegates need a manageable catalog (own or an active
  // full delegation), which `manageableCatalogs` enumerates — the same source
  // the Albums page gate and the Create Album dialog use.
  const canManageAlbums = !isViewer && (!isDelegate || manageableCatalogs.length > 0);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <IconButton
        onClick={handleClick}
        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        data-testid="user-profile-button"
        aria-label="User profile menu"
      >
        <Avatar sx={{ width: 32, height: 32 }}>
          {getInitials(user?.firstName, user?.lastName)}
        </Avatar>
        {!isMobile && <Typography variant="body2">{getDisplayName()}</Typography>}
        <KeyboardArrowDown />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ mt: 1 }}
      >
        {/* User Info Section */}
        <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ width: 24, height: 24 }}>
            {getInitials(user?.firstName, user?.lastName)}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight="medium">{getDisplayName()}</Typography>
            {!isViewer && (
              <Typography variant="caption" color="text.secondary">
                {en.userProfile.artLegacyId} {aliIdLoading ? 'Loading...' : (aliId || en.userProfile.notAssigned)}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" display="block">
              {user?.email || en.userProfile.noEmail}
            </Typography>
          </Box>
        </Box>

        <Divider />

        {/* Admin Panel - Only show for admins */}
        {isAdmin && (
          <MenuItem onClick={handleAdmin}>
            <AdminPanelSettings sx={{ mr: 2 }} />
            {en.navigation.adminPanel}
          </MenuItem>
        )}

        {/* Collections - Show for owners, admins, and full delegates */}
        {canManageAlbums && (
          <MenuItem onClick={handleCollections}>
            <CollectionsBookmark sx={{ mr: 2 }} />
            {en.navigation.catalogs}
          </MenuItem>
        )}

        {/* Activities - Show for everyone except viewers */}
        {!isViewer && (
          <MenuItem onClick={handleActivities}>
            <Timeline sx={{ mr: 2 }} />
            {en.navigation.activities}
          </MenuItem>
        )}

        {/* CV/About - Hide for viewers only; delegates can view the owner's CV */}
        {!isViewer && (
          <MenuItem onClick={handleCurriculumVitae}>
            <Person sx={{ mr: 2 }} />
            {isCommercial ? en.profile.commercial.about : en.profile.curriculumVitae}
          </MenuItem>
        )}
        <MenuItem onClick={handleSettings}>
          <Settings sx={{ mr: 2 }} />
          {en.profile.settings}
        </MenuItem>
        <MenuItem onClick={handleBetaFeedback}>
          <RateReviewOutlined sx={{ mr: 2 }} />
          {en.footer.betaFeedback}
        </MenuItem>
        <ThemeToggle variant="menuItem" onAfterToggle={handleClose} />

        <Divider />

        <MenuItem onClick={handleLogout}>
          <Logout sx={{ mr: 2 }} />
          {en.profile.logout}
        </MenuItem>
      </Menu>
    </Box>
  );
};
